import React from 'react';
import { MessageCircle, Calendar, CheckCircle, Shield, Clock, Heart, Home, Building2, Archive } from 'lucide-react';
import ServicePageLayout from '../components/ServicePageLayout';
import { P } from '../constants';

const data = {
  seo: {
    title: 'Nachlassräumung Wien » Diskret & einfühlsam – Wohnungsräumung Wien',
    description: 'Nachlassräumung Wien nach Todesfall ✓ Diskret & einfühlsam ✓ Fixpreisgarantie ✓ Kurzfristige Termine. Wir nehmen Ihnen die Last ab. Jetzt anfragen!',
    keywords: 'Nachlassräumung Wien, Wohnungsräumung nach Todesfall Wien, Nachlassräumung Kosten Wien, Nachlasswohnung räumen Wien',
    canonical: '/nachlassraeumung-wien',
    h1short: 'Nachlassräumung Wien',
  },
  schemas: [{
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Nachlassräumung Wien',
    'provider': { '@type': 'LocalBusiness', 'name': 'Wohnungsräumung Wien' },
    'areaServed': 'Wien',
    'description': 'Diskrete, einfühlsame Nachlassräumung in Wien nach einem Todesfall. Mit Fixpreisgarantie und kostenloser Besichtigung.',
    'offers': { '@type': 'Offer', 'price': '490', 'priceCurrency': 'EUR' },
  }],
  hero: {
    badge: 'Diskreter Notfallservice',
    headline: <><span>Nachlassräumung<br />Wien</span><br /><span className="text-[#E8722A]">diskret & einfühlsam.</span></>,
    subline: 'Jemanden zu verlieren ist schwer genug.',
    bullets: [
      'Fotos, Schmuck, Erinnerungsstücke – sorgfältig aussortiert',
      'Absolut diskret – kein Aufsehen, keine Dritten informiert',
      'Kurzfristige Termine – auch 24h-Notfallservice',
      'Verbindlicher Fixpreis nach kostenloser Besichtigung',
      'Sie müssen nicht dabei sein. Wir erledigen alles.',
    ],
    imageSrc: P.emptyRoom,
    imageAlt: 'Nachlassräumung Wien – Leere Wohnung nach Räumung',
  },
  formTitle: 'Nachlassräumung anfragen –',
  serviceName: 'Nachlassräumung',
  serviceCard: {
    heading: 'Nachlassräumung Wien – mit Würde und ohne Aufwand für Sie',
    paragraphs: [
      'Jemanden zu verlieren ist schwer genug. Die Wohnung danach zu räumen sollte nicht auch noch Ihre Last sein.',
      'Wir kommen diskret, gehen behutsam vor – Fotos, Schmuck und Erinnerungsstücke werden sorgfältig aussortiert und Ihnen übergeben. Den Rest erledigen wir. Sie müssen nicht dabei sein.',
    ],
    listItems: [
      'Fotos, Schmuck, Erinnerungsstücke – sorgfältig aussortiert & übergeben',
      'Absolut diskret – kein Aufsehen bei Nachbarn oder Dritten',
      'Kurzfristige Termine – auch 24h-Notfallservice möglich',
      'Verbindlicher Fixpreis nach kostenloser Besichtigung',
      'Besenreine Übergabe – Sie müssen nicht anwesend sein',
    ],
    imageSrc: P.processBoxes,
    imageAlt: 'Nachlassräumung Wien – professionell und diskret',
  },
  benefits: [
    { icon: <Heart size={22} />, title: 'Einfühlsam', desc: 'Wir handeln mit Respekt und Würde – besonders bei persönlichen Gegenständen und Erinnerungsstücken.' },
    { icon: <Shield size={22} />, title: 'Diskret', desc: 'Absolute Diskretion zu jedem Zeitpunkt. Keine Weitergabe von Informationen.' },
    { icon: <Clock size={22} />, title: 'Kurzfristig', desc: 'Nachlassräumungen werden bevorzugt behandelt – oft innerhalb von 24–48 Stunden.' },
    { icon: <CheckCircle size={22} />, title: 'Vollständig', desc: 'Von der ersten Bewertung bis zur besenreinen Übergabe – alles aus einer Hand.' },
  ],
  process: [
    { icon: <MessageCircle size={24} />, title: 'Diskreter Erstkontakt', desc: 'Anruf oder Formular – wir gehen sofort auf Ihre Situation ein und vereinbaren einen passenden Termin.' },
    { icon: <Calendar size={24} />, title: 'Besichtigung & Einfühlsames Gespräch', desc: 'Wir besichtigen die Wohnung, sortieren gemeinsam mit Ihnen Wertgegenstände aus und erstellen den Fixpreis.' },
    { icon: <CheckCircle size={24} />, title: 'Räumung & besenreine Übergabe', desc: 'Professionelle Räumung mit besenreiner Übergabe – Sie müssen nicht anwesend sein.' },
  ],
  pricing: [
    { label: 'Kleine Nachlasswohnung', price: 'ab €490', icon: <Home size={20} />, highlight: false },
    { label: 'Mittlere Nachlasswohnung', price: 'ab €890', icon: <Building2 size={20} />, highlight: true },
    { label: 'Großer Nachlass / Haus', price: 'auf Anfrage', icon: <Archive size={20} />, highlight: false },
  ],
  faq: [
    { q: 'Was ist eine Nachlassräumung?', a: 'Die Räumung einer Wohnung nach dem Tod einer Person. Aussortieren, Entsorgen, Übergeben. Wir übernehmen das – so diskret und behutsam wie möglich.' },
    { q: 'Wie schnell können Sie kommen?', a: 'Nachlassräumungen behandeln wir bevorzugt. In dringenden Fällen sind wir oft innerhalb von 24–48 Stunden vor Ort. Einfach anrufen.' },
    { q: 'Was passiert mit persönlichen Sachen und Erinnerungsstücken?', a: 'Die gehören Ihnen. Fotos, Schmuck, Briefe – wir sortieren das sorgfältig aus und übergeben es Ihnen, bevor der Rest entsorgt wird. Kein Stück geht verloren.' },
    { q: 'Müssen Angehörige dabei sein?', a: 'Nein. Die meisten möchten das nicht. Wir erledigen alles in Ihrem Auftrag, übergeben besenrein – und berichten Ihnen danach kurz, was war.' },
    { q: 'Was kostet eine Nachlassräumung?', a: 'Ab €490 für kleine Wohnungen, ab €890 für größere. Nach der kostenlosen Besichtigung erhalten Sie einen verbindlichen Fixpreis.' },
  ],
  relatedServices: [
    { title: 'Wohnungsräumung', slug: '/wohnungsraeumung-wien', icon: <Home size={18} /> },
    { title: 'Haushaltsauflösung', slug: '/haushaltsaufloesung-wien', icon: <Archive size={18} /> },
    { title: 'Messie-Wohnung', slug: '/messie-wohnung-wien', icon: <Building2 size={18} /> },
    { title: 'Kellerräumung', slug: '/kellerraeumung-wien', icon: <CheckCircle size={18} /> },
  ],
};

export default function NachlassraeumungPage() {
  return <ServicePageLayout data={data} />;
}
